import styled from '@emotion/styled'
import { SpacerDefs, SpacerDefsPadding, spacerPaddings } from '@pharmac/ui'
import { ReactElement, useEffect, useState } from 'react'
export type TabProps = {
  handle: string | ReactElement | Function
}

export type TabContainerProps = {
  padding?: string
  handle?: string | ReactElement | Function
}

export type TabhandleProps = {
  handle: string | ReactElement | Function
  index: number
  selectedTab: number
  onClick: () => void
  singleTab?: boolean
}

export type TabListItemProps = {
  selected: boolean
  singleTab?: boolean
}

export type TabsChildrenObject = TabProps & {
  index: number
  element: ReactElement
  onClick?: () => void
}

export type TabsProps = SpacerDefs & {
  padding?: string
  tabs: TabsChildrenObject[]
  singleTab?: boolean
  childrenAreAlwaysRendered?: boolean
  selectedIndex?: number
}

const ExtranetTabsContainer = styled.div`
  font-family: var(--font-family-primary);
`
const ExtranetTabContainer = styled.div<TabContainerProps & SpacerDefsPadding>`
  background: var(--tabs-content-background);
  overflow: hidden;
  border-radius: var(--tabs-container-border-radius);

  ${spacerPaddings}
`

const PhDiv = styled.div`
  display: flex;
  justify-content: left;
`
const ExtranetTabsList = styled.ul<{ singleTab?: boolean }>`
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  flex-wrap: nowrap;
  background: ${(props) => (props.singleTab ? '' : 'var(--tabs-list-background)')};
  color: var(--tabs-list-color);
  border-radius: var(--tabs-list-item-border-radius);
  background-clip: padding-box;
`
const ExtranetTabsListItem = styled.li<TabListItemProps>`
  list-style-type: none;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: var(--tabs-list-item-padding);
  transition: var(--tabs-list-item-transition);
  transform-origin: bottom left;
  scale: ${(props) => (props.selected ? '1.1' : '1')};
  z-index: ${(props) => (props.selected ? '3' : '1')};
  font-size: var(--tabs-list-item-font-size);
  font-family: var(--tabs-list-item-font-face);
  font-weight: ${(props) =>
    props.selected
      ? 'var(--tabs-list-item-selected-font-weight)'
      : 'var(--tabs-list-item-font-weight)'};
  color: ${(props) =>
    props.selected ? 'var(--tabs-list-item-selected-color)' : 'var(--tabs-list-color)'};
  user-select: none;
  box-shadow: ${(props) => (props.selected ? 'none' : 'var(--tabs-list-item-box-shadow)')};
  background: ${(props) =>
    props.selected
      ? 'var(--tabs-list-item-selected-background)'
      : 'var(--tabs-list-item-background)'};
  border-radius: var(--tabs-list-item-border-radius);
  position: relative;
  cursor: pointer;
  background-clip: padding-box;

  &:first-of-type {
    margin-right: ${(props) => (props.selected ? 'var(--spacing-medium)' : 'inherit')};
  }
  &:last-of-type {
    margin-left: ${(props) =>
      props.selected && !props.singleTab ? 'var(--spacing-medium)' : props.singleTab && ''};
  }
  &:not(:first-of-type):not(:last-of-type) {
    margin: ${(props) => (props.selected ? '0 var(--spacing-medium)' : 'inherit')};
  }

  &:before,
  &:after {
    position: absolute;
    bottom: 0;
  }

  &:before,
  &:after {
    background: ${(props) =>
      props.selected
        ? 'var(--tabs-list-item-selected-background)'
        : 'var(--tabs-list-item-background)'};
    width: 11px;
    height: 10px;
    content: '';
    z-index: 1;
  }

  &:before {
    left: -10px;
  }

  &:after {
    right: -10px;
  }

  &:first-of-type:before {
    width: 10px;
  }

  &:first-of-type a:before,
  &:first-of-type:before,
  &:last-of-type a:after {
    background-color: var(--color-accent-1);
  }
`
const ItemContainer = styled.a`
  display: inline-flex;
  align-items: center;
  &:before,
  &:after {
    position: absolute;
    bottom: 0;
    height: 20px;
    width: 20px;
    border-radius: 10px;
    background: var(--tabs-list-background);
    z-index: 2;
    content: '';
  }
  &:before {
    left: -20px;
  }
  &:after {
    right: -20px;
  }
`
const TabVisibilityDeterminer = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? 'inherit' : 'none')};
`

export const ExtranetTab = styled.div<TabContainerProps>`
  padding: ${(props) => (props.padding ? props.padding : 'var(--tabs-content-padding)')};
`
export const ExtranetTabTitle = ({
  handle,
  onClick,
  index,
  selectedTab,
  singleTab,
}: TabhandleProps) => {
  return (
    <ExtranetTabsListItem
      selected={selectedTab === index}
      onClick={onClick}
      singleTab={singleTab}
    >
      <ItemContainer>{typeof handle === 'function' ? handle() : handle}</ItemContainer>
    </ExtranetTabsListItem>
  )
}

export const ExtranetTabs = (props: TabsProps & SpacerDefsPadding) => {
  const {
    tabs,
    childrenAreAlwaysRendered = false,
    singleTab,
    selectedIndex,
    ...paddingProps
  } = props

  const [selectedTab, setSelectedTab] = useState<number | undefined>(selectedIndex ?? 0)

  useEffect(() => {
    if (selectedTab !== selectedIndex) {
      setSelectedTab(typeof selectedIndex === 'number' ? selectedIndex : tabs[0].index)
    }
  }, [selectedIndex])

  const handleClick = (tab: TabsChildrenObject) => {
    setSelectedTab(tab.index)

    if (typeof tab.onClick === 'function') tab.onClick()
  }

  if (typeof selectedIndex !== 'number') return null

  return (
    <ExtranetTabsContainer>
      <PhDiv>
        <ExtranetTabsList singleTab={singleTab}>
          {tabs.map((tab) => (
            <ExtranetTabTitle
              singleTab={singleTab}
              key={tab.index}
              handle={tab.handle}
              index={tab.index}
              selectedTab={selectedTab as number}
              onClick={() => handleClick(tab)}
            />
          ))}
        </ExtranetTabsList>
      </PhDiv>
      <ExtranetTabContainer {...paddingProps}>
        {childrenAreAlwaysRendered
          ? tabs.map((child) => (
              <TabVisibilityDeterminer
                key={child.index}
                isVisible={child.index === selectedTab}
              >
                {child.element}
              </TabVisibilityDeterminer>
            ))
          : tabs[selectedTab as number].element}
      </ExtranetTabContainer>
    </ExtranetTabsContainer>
  )
}
